// Form Fill — plan §4.4 + §6.
// S1: 3 kolon + 3 renderer + debounce save.
// S2: 6 renderer + 8-state matrix + offline queue + bölüm progress %.
// S3: presigned upload + FileRenderer.
// S4: missing panel + prior-year + consultant note + SSE presence.

import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  formatReportingPeriod,
  readCompositeParts,
  type PresenceEvent,
  type QuestionSnapshot,
  formatKpiNumber,
} from "@mahir/shared";
import { GenerateConfirmModal } from "../components/generation/GenerateConfirmModal";
import { selectNote, selectValue, useFormFillStore } from "../stores/form-fill-store";
import { selectHeldInvalidCodes, selectStuckUnsavedCount } from "../stores/form-fill-flush";
import { FORM_SAVE_COPY } from "../lib/form-save-copy";
import { useAppStore } from "../stores/app-store";
import { useAuthStore } from "../stores/auth-store";
import { ApiError } from "../api/client";
import { submitParticipation } from "../api/participants";
import { setLastClientSubmission } from "../lib/client-submission";
import { SectionNav } from "../components/form-fill/SectionNav";
import { FormSaveIndicator } from "../components/form-fill/SaveIndicator";
import { SupersededNotice } from "../components/form-fill/SupersededNotice";
import { MissingPanel } from "../components/form-fill/MissingPanel";
import { MissingPanelAside } from "../components/form-fill/MissingPanelAside";
import { PriorYearHint } from "../components/form-fill/PriorYearHint";
import { OutlierNotice } from "../components/form-fill/OutlierNotice";
import { KpiConsistencyHint } from "../components/form-fill/KpiConsistencyHint";
import { ConsultantNote } from "../components/form-fill/ConsultantNote";
import { EvidenceUpload } from "../components/form-fill/EvidenceUpload";
import { PresenceBanner } from "../components/form-fill/PresenceBanner";
import { QuestionInput } from "../components/form-fill/QuestionInput";
import { ProvenanceBar } from "../components/form-fill/ProvenanceBar";
import { ProvenanceBadge } from "../components/form-fill/ProvenanceBadge";
import { ProvenanceLine } from "../components/form-fill/ProvenanceLine";
import { ProvenanceFilterList } from "../components/form-fill/ProvenanceFilterList";
import { SectionPager } from "../components/form-fill/SectionPager";
import { useMediaQuery } from "../hooks/use-media-query";
import { useOutlierNotice } from "../hooks/use-outlier-notice";
import { NARROW_QUERY } from "../lib/shell-layout";
import { formPeriodDateLine, priorPeriodMissingNote } from "../lib/period-ui";
import {
  IMPORT_COPY,
  importableQuestionTotal,
  isEditedAfterImport,
  proposalFormValue,
  provenanceLineKind,
} from "../lib/report-import-ui";
import {
  useProvenanceDerivations,
  type ProvenanceCardContext,
} from "../hooks/use-provenance-derivations";

// Müşteri Katılımcı Rolü (Task 8) — ilk açılışta bir kez gösterilen bilgi bandı
// (design-ref D9). Global anahtar: "İlk açılışta bir kez" tek seferlik ipucu olarak
// okundu, proje bazlı DEĞİL — bir müşteri kaç farklı form doldurursa doldursun bir
// kez görür yeterli.
const CLIENT_INFO_BANNER_KEY = "mahir.client.form-info-dismissed";

// Fix Turu 1 (IMPORTANT-2) — design-ref "Mobil kararı (D7)": uzun formu dar
// ekranda (telefon/küçük tablet) doldurmanın rahatsız olabileceğine dair ayrı,
// tek seferlik kapatılabilir bir bant. D9'dan AYRI key — ikisi bağımsız
// kapatılır. Sadece dar viewport'ta anlamlı olduğu için matchMedia ile
// koşullanır (geniş ekranda "geniş ekran kullanın" notu anlamsız olurdu).
const CLIENT_WIDESCREEN_HINT_KEY = "mahir.client.form-widescreen-hint-dismissed";

// Task 11 / 2D — "geçen yılın aynı dönemi yok" satırı proje bazlı kapatılır.
const PERIOD_HINT_DISMISS_PREFIX = "period-hint-dismissed:";

// SSR/private mode/kota: okunamıyorsa satırı gösterme tarafına düşme (false).
function readPeriodNoteDismissed(projectId: string): boolean {
  if (typeof window === "undefined") return true;
  try {
    return window.localStorage.getItem(`${PERIOD_HINT_DISMISS_PREFIX}${projectId}`) === "1";
  } catch {
    return false;
  }
}

export function FormFill(): JSX.Element {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const userKind = useAuthStore((s) => s.user?.userKind);
  const userId = useAuthStore((s) => s.user?.id);
  const isClient = userKind === "client";
  // Preview modu (readonly assignee-görünümü) internal'a özgü — client'ta URL'e
  // elle ?preview=1 eklense bile hiç honoring edilmez (toggle butonu zaten yok).
  const isPreview = !isClient && searchParams.get("preview") === "1";
  const [generateModalOpen, setGenerateModalOpen] = useState(false);
  const [submitModalOpen, setSubmitModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const submittingRef = useRef(false);
  const [infoBannerVisible, setInfoBannerVisible] = useState(
    () =>
      isClient &&
      typeof window !== "undefined" &&
      window.localStorage.getItem(CLIENT_INFO_BANNER_KEY) !== "1",
  );
  const [priorPeriodNoteDismissed, setPriorPeriodNoteDismissed] = useState(() =>
    readPeriodNoteDismissed(id),
  );
  // Lazy init yalnız ilk mount'ta koşar; aynı FormFill instance'ı başka bir projeye
  // geçerse (route param değişimi) önceki projenin kapatma durumu taşınırdı.
  useEffect(() => {
    setPriorPeriodNoteDismissed(readPeriodNoteDismissed(id));
  }, [id]);
  const [wideScreenHintVisible, setWideScreenHintVisible] = useState(
    () =>
      isClient &&
      typeof window !== "undefined" &&
      window.matchMedia(NARROW_QUERY).matches &&
      window.localStorage.getItem(CLIENT_WIDESCREEN_HINT_KEY) !== "1",
  );
  const applyClientPanelDefaults = useAppStore((s) => s.applyClientPanelDefaults);
  const setNarrowFormPanels = useAppStore((s) => s.setNarrowFormPanels);
  const isNarrow = useMediaQuery(NARROW_QUERY);
  const snapshot = useFormFillStore((s) => s.snapshot);
  const priorYearByCode = useFormFillStore((s) => s.priorYearByCode);
  const priorYearLoaded = useFormFillStore((s) => s.priorYearLoaded);
  const activeSectionId = useFormFillStore((s) => s.activeSectionId);
  const saveState = useFormFillStore((s) => s.saveState);
  const conflictDetails = useFormFillStore((s) => s.conflictDetails);
  const errorMessage = useFormFillStore((s) => s.errorMessage);
  const load = useFormFillStore((s) => s.load);
  const setActiveSection = useFormFillStore((s) => s.setActiveSection);
  const dismissConflict = useFormFillStore((s) => s.dismissConflict);
  const flushNow = useFormFillStore((s) => s.flushNow);
  const handleOnlineEvent = useFormFillStore((s) => s.handleOnlineEvent);
  const handleOfflineEvent = useFormFillStore((s) => s.handleOfflineEvent);
  const missingCount = useFormFillStore((s) => s.missingFields.length);
  // UX P1 K4/Ö7 — sunucunun reddettiği alanlar + kendiliğinden kaydedilmeyecek değişiklikler
  const stuckUnsavedCount = useFormFillStore(selectStuckUnsavedCount);
  const presenceOthers = useFormFillStore((s) => s.presenceOthers);
  const setPresenceOthers = useFormFillStore((s) => s.setPresenceOthers);
  const [missingOpen, setMissingOpen] = useState(false);
  const uiDensity = useAppStore((s) => s.uiDensity);
  const toggleUiDensity = useAppStore((s) => s.toggleUiDensity);
  const undoValue = useFormFillStore((s) => s.undoValue);
  const redoValue = useFormFillStore((s) => s.redoValue);
  // Köken (Task 15) — client'ta store zaten hiç doldurulmaz (load() fetch atmaz).
  // DİKKAT (fix round 1): burada `responsesByCode`/`pending` NESNELERİNE abone OLMA —
  // her tuşta tüm sayfa + tüm kartlar render edilir. Türetmeler
  // `useProvenanceDerivations` içinde primitif imza üzerinden yapılır.
  const provenance = useFormFillStore((s) => s.provenance);
  const provenanceError = useFormFillStore((s) => s.provenanceError);
  const provenanceFilter = useFormFillStore((s) => s.provenanceFilter);
  const setProvenanceFilter = useFormFillStore((s) => s.setProvenanceFilter);
  const reloadProvenance = useFormFillStore((s) => s.reloadProvenance);
  const setPinnedCode = useFormFillStore((s) => s.setPinnedCode);
  const canOpenReport = useAuthStore((s) => s.user?.permissions["projects.import_report"] === true);

  useEffect(() => {
    if (id) void load(id, { isClient });
  }, [id, load, isClient]);

  // Client formu daha dar/sade açılsın (design-ref Mobil kararı D7) — sadece
  // kullanıcı daha önce panelleri hiç toggle etmediyse varsayılan daraltılmış başlar.
  // Finar UX P3 Ö6: ≤1024 px'te iç kullanıcıda da iki yan panel daraltılmış açılır (persist
  // etmez); geniş ekrana dönünce kalıcı tercih geri gelir, sonra client varsayılanı uygulanır.
  useEffect(() => {
    setNarrowFormPanels(isNarrow);
    if (isClient && !isNarrow) applyClientPanelDefaults();
    return () => setNarrowFormPanels(false); // global store: sayfadan çıkınca dar kip kalmasın
  }, [isClient, isNarrow, applyClientPanelDefaults, setNarrowFormPanels]);

  // SSE presence — EventSource ile bağlan
  // Client sade-shell'de danışman adları gösterilmez (dar-DTO felsefesiyle
  // tutarlı) — bağlantı client modunda hiç açılmaz.
  useEffect(() => {
    if (!id || isClient) return;
    const es = new EventSource(`/api/v1/projects/${id}/presence/events`, {
      withCredentials: true,
    });
    const onPresence = (ev: MessageEvent<string>): void => {
      try {
        const data = JSON.parse(ev.data) as PresenceEvent;
        setPresenceOthers(data.others);
      } catch {
        // ignore
      }
    };
    es.addEventListener("presence", onPresence as EventListener);
    return () => {
      es.removeEventListener("presence", onPresence as EventListener);
      es.close();
      setPresenceOthers([]);
    };
  }, [id, isClient, setPresenceOthers]);

  // Online/offline listener
  useEffect(() => {
    window.addEventListener("online", handleOnlineEvent);
    window.addEventListener("offline", handleOfflineEvent);
    return () => {
      window.removeEventListener("online", handleOnlineEvent);
      window.removeEventListener("offline", handleOfflineEvent);
    };
  }, [handleOnlineEvent, handleOfflineEvent]);

  // Sayfadan ayrılırken bekleyen değişiklikleri flush'la (best-effort)
  useEffect(() => {
    return () => {
      void flushNow();
    };
  }, [flushNow]);

  // M9-S3: Sonraki section'a geçmek için helper (Cmd+Enter + buton)
  const goNextSection = useCallback(() => {
    const snap = useFormFillStore.getState().snapshot;
    const active = useFormFillStore.getState().activeSectionId;
    if (!snap || !active) return;
    const ordered = [...snap.snapshot.sections].sort((a, b) => a.order - b.order);
    const idx = ordered.findIndex((s) => s.id === active);
    const next = ordered[idx + 1];
    if (next) {
      // Form paneli başa alma SectionPager'da (kaydırılan <main>, window değil — Finar İ9).
      useFormFillStore.getState().setActiveSection(next.id);
    }
  }, []);

  // M9-S3: Focused input'un questionCode'unu (data-question-code attribute'u) okur.
  const activeQuestionCode = (): string | null => {
    const el = document.activeElement;
    if (!(el instanceof HTMLElement)) return null;
    const container = el.closest<HTMLElement>("[data-question-code]");
    return container?.dataset.questionCode ?? null;
  };

  // M9-S3: Klavye kısayolları (§6.5) + Undo/Redo (§6.6 Cmd+Z).
  useEffect(() => {
    const handler = (e: KeyboardEvent): void => {
      const mod = e.metaKey || e.ctrlKey;
      if (!mod) return;
      // Cmd+S manuel flush
      if ((e.key === "s" || e.key === "S") && !e.shiftKey) {
        e.preventDefault();
        void flushNow();
        return;
      }
      // Cmd+Enter sonraki bölüm
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        goNextSection();
        return;
      }
      // Cmd+Z undo
      if ((e.key === "z" || e.key === "Z") && !e.shiftKey) {
        const code = activeQuestionCode();
        if (code && useFormFillStore.getState().canUndo(code)) {
          e.preventDefault();
          undoValue(code);
        }
        return;
      }
      // Cmd+Shift+Z redo
      if ((e.key === "z" || e.key === "Z") && e.shiftKey) {
        const code = activeQuestionCode();
        if (code && useFormFillStore.getState().canRedo(code)) {
          e.preventDefault();
          redoValue(code);
        }
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [flushNow, goNextSection, undoValue, redoValue]);

  // --- Köken (Task 15) — client dalında HİÇBİRİ render edilmez, fetch de yapılmaz.
  // Tüm türetimler saf yardımcılardan (report-import-ui.ts) gelir.
  // run2-3 inceleme (4) — bandın paydası JobsTab/ProjectDetail ile aynı kuralda:
  // dosya tipi soru aktarım kapsamı dışıdır, paydaya girmez.
  const importableTotal = useMemo(
    () => (snapshot ? importableQuestionTotal(snapshot.snapshot.questions) : 0),
    [snapshot],
  );

  const questionCodesInOrder = useMemo(() => {
    if (!snapshot) return [] as { code: string; sectionId: string }[];
    const sectionOrder = new Map(snapshot.snapshot.sections.map((s) => [s.id, s.order]));
    return (
      snapshot.snapshot.questions
        // run3 K1/N6 — ÇİP EVRENİ bant paydasıyla aynı: `file` tipi soru aktarılamaz
        // (D7'den beri çıkarım isteminden de çıkıyor), bu yüzden "Cevaplanmamış"
        // çipinde de sayılmaz. Aksi halde aynı ekranda 84 ↔ 83 iki toplam dolaşıyordu.
        .filter((q) => q.enabled && q.type !== "file")
        .slice()
        .sort(
          (a, b) =>
            (sectionOrder.get(a.sectionId) ?? 0) - (sectionOrder.get(b.sectionId) ?? 0) ||
            a.order - b.order,
        )
        .map((q) => ({ code: q.code, sectionId: q.sectionId }))
    );
  }, [snapshot]);

  // Türetmeler (çip sayıları, filtre listesi, kart bağlamı) ayrı hook'ta — abonelik
  // yalnız primitif imza üzerinden, köken yoksa hiç koşmaz (fix round 1 / R-perf).
  const derived = useProvenanceDerivations({
    questionCodesInOrder,
    canOpenReport,
    enabled: !isClient,
  });

  if (!snapshot) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: "var(--color-bg)", color: "var(--color-text-2)" }}
      >
        {errorMessage ?? "Form yükleniyor…"}
      </div>
    );
  }

  const { project } = snapshot;
  // Raporlama dönemi (3A) — yıllıkta ikisi de null: pill ve açıklama satırı YOK,
  // üst bar bugünküyle birebir aynı kalır.
  const periodLabel =
    project.periodType === "annual"
      ? null
      : formatReportingPeriod(project.reportingYear, project.periodType);
  const periodDateLine = formPeriodDateLine(project.reportingYear, project.periodType);
  // Çeyreklik + öneri fetch'i bitti + hiç öneri yok + kapatılmadı → tek satır.
  const priorPeriodNote =
    priorYearLoaded && !priorPeriodNoteDismissed && Object.keys(priorYearByCode).length === 0
      ? priorPeriodMissingNote(project.reportingYear, project.periodType)
      : null;
  const { sections, questions } = snapshot.snapshot;
  const activeQuestions = [...questions]
    .filter((q) => q.sectionId === activeSectionId && q.enabled)
    .sort((a, b) => a.order - b.order);
  const activeSection = sections.find((s) => s.id === activeSectionId);
  // 2A düz mod — filtrelenmiş kodlar snapshot sırasında soru nesnelerine çevrilir.
  // `derived.filteredCodes === null` → çip "Tümü" (ya da köken yok): düz liste yok.
  const filteredQuestions =
    derived.filteredCodes === null
      ? []
      : (() => {
          const byCode = new Map(questions.filter((q) => q.enabled).map((q) => [q.code, q]));
          return derived.filteredCodes
            .map((c) => byCode.get(c))
            .filter((q): q is QuestionSnapshot => q !== undefined);
        })();

  // Müşteri Katılımcı Rolü (Task 8) — Submit akışı (D8 + eng-review D7/D13).
  // 1) buton tıklanınca ÖNCE flushNow() — debounce kuyruğundaki son cevaplar submit'ten
  //    önce mutlaka kaydedilir (yoksa submit sonrası PUT 403 yiyip veri sessizce kaybolur).
  // 2) flush sonrası bekleyen/başarısız kayıt kaldıysa (offline/retry/conflict) submit
  //    ATILMAZ — kullanıcıya net mesaj, buton tekrar aktif.
  // 3) flush + missing refresh temizse confirm modal açılır (eksik varsa ek satır).
  // submittingRef: React state güncellemesi bir sonraki render'a kadar gecikebileceği
  // için çift-tık senaryosunda state kontrolü tek başına yetmez — senkron ref guard.
  async function handleSubmitClick(): Promise<void> {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    setSubmitError(null);
    await flushNow();
    await useFormFillStore.getState().refreshMissing();
    const s = useFormFillStore.getState();
    if (s.saveState === "conflict") {
      submittingRef.current = false;
      setSubmitting(false);
      setSubmitError("Danışmanınız bu cevabı güncelledi — sayfayı yenileyin");
      return;
    }
    if (Object.keys(s.pending).length > 0 || s.saveState === "offline" || s.saveState === "retry") {
      submittingRef.current = false;
      setSubmitting(false);
      setSubmitError(FORM_SAVE_COPY.submitBlocked(selectHeldInvalidCodes(s).length > 0));
      return;
    }
    submittingRef.current = false;
    setSubmitting(false);
    setSubmitModalOpen(true);
  }

  async function handleConfirmSubmit(): Promise<void> {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    try {
      await submitParticipation(id);
      // Fix Turu 1 (IMPORTANT-1) — cache kullanıcı id'sine namespace'lenir; client
      // route'a girmek zaten authenticated olmayı gerektirdiği için userId burada
      // her zaman dolu olmalı, ama savunmacı olarak yoksa yazma atlanır (ClientHome
      // zaten kendi tarafında userId yoksa okumaz — güvenli fallback jenerik ekran).
      if (userId) {
        setLastClientSubmission(userId, {
          projectId: id,
          frameworkName: project.frameworkName,
          reportingYear: project.reportingYear,
          // F2 — dönem de yazılır; ClientHome teşekkür ekranı doğru etiketi gösterir.
          periodType: project.periodType,
          submittedAt: new Date().toISOString(),
        });
      }
      void navigate("/", { replace: true });
    } catch (err) {
      submittingRef.current = false;
      setSubmitting(false);
      setSubmitModalOpen(false);
      // PARTICIPATION_ENDED ise api/client.ts merkezi kickout'u zaten ClientHome'a
      // yönlendirdi (mesajıyla) — burada ikinci/çelişen bir hata göstermiyoruz.
      if (err instanceof ApiError && err.code === "PARTICIPATION_ENDED") return;
      setSubmitError(err instanceof ApiError ? err.message : "Gönderilemedi — tekrar deneyin");
    }
  }

  return (
    <div
      /* `min-h-screen` KALDIRILDI (fix round 1): min-height:100vh aşağıdaki
         `calc(100vh - shell header)` yüksekliğini eziyor ve shell başlığı kadar
         dikey taşma üretiyordu. Yükseklik artık tek yerde, aşağıdaki style'da. */
      data-density={uiDensity}
      style={{
        background: "var(--color-bg)",
        fontFamily: "var(--font-sans)",
        // 2A — dikey flex kolon: form üst barı + bantlar kendi yüksekliklerini alır,
        // 3-kolon çalışma alanı kalanı doldurur (kolonlar kendi içinde scroll'lanır).
        // Yükseklik SHELL'in üst çubuğunu düşer (AppShell 56 px / ClientShell 60 px —
        // değer shell'in kendi `--layout-shell-header` değişkeninden gelir); düz
        // `100vh` shell başlığı kadar taşma üretirdi.
        display: "flex",
        flexDirection: "column",
        height: "calc(100vh - var(--layout-shell-header, 0px))",
      }}
    >
      {/* Üst bar */}
      <header
        style={{
          borderBottom: "1px solid var(--color-border)",
          background: "var(--color-surface)",
          padding: "12px 24px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 16,
        }}
      >
        <div className="min-w-0 flex items-center gap-3">
          {/* D9 — "←" client'ta RENDER EDİLMEZ: hedefi /projects/:id client whitelist
              dışı (CLIENT_FORBIDDEN → redirect döngüsü tuzağı). ClientHome'a dönüş
              ClientShell logosuyla. */}
          {!isClient && (
            <Link
              to={`/projects/${id}`}
              style={{ color: "var(--color-primary)", fontSize: "0.85rem" }}
            >
              ←
            </Link>
          )}
          <div className="min-w-0">
            {/* Client dar DTO'sunda clientName 2026-09-30'dan beri VAR (aynı kişi
                birden fazla firmaya davet edilebilir) — yoksa segment atlanır.
                3A: çeyreklikte dönem üst barda PILL olarak çıkar (uzun form) ve
                kırılım satırından düşer. YILLIKTA markup bugünküyle BİREBİR aynı
                kalır (flex sarmalayıcı/span yok) — bu dalı sadeleştirme. */}
            {periodLabel === null ? (
              <div style={{ color: "var(--color-text-3)", fontSize: "0.72rem" }}>
                {isClient
                  ? [project.clientName, project.frameworkName, project.reportingYear.toString()]
                      .filter((s): s is string => Boolean(s))
                      .join(" · ")
                  : `${project.clientName} · ${project.frameworkName} · ${project.reportingYear}`}
              </div>
            ) : (
              <div
                className="flex items-center gap-2 flex-wrap"
                style={{ color: "var(--color-text-3)", fontSize: "0.72rem" }}
              >
                <span>
                  {isClient
                    ? [project.clientName, project.frameworkName]
                        .filter((s): s is string => Boolean(s))
                        .join(" · ")
                    : `${project.clientName} · ${project.frameworkName}`}
                </span>
                <span
                  aria-describedby="form-period-range"
                  style={{
                    padding: "2px 10px",
                    borderRadius: 999,
                    background: "color-mix(in srgb, var(--color-primary) 10%, white)",
                    color: "var(--color-primary)",
                    fontWeight: 600,
                  }}
                >
                  {periodLabel}
                </span>
              </div>
            )}
            <div
              style={{
                fontFamily: "var(--font-display)",
                fontSize: "1.05rem",
                color: "var(--color-text)",
              }}
            >
              {activeSection ? `${activeSection.code} · ${activeSection.label}` : "Form"}
            </div>
            {/* Yalnız çeyreklikte: "2. Çeyrek = 1 Ocak – 30 Haziran 2025 (kümülatif ara dönem)" */}
            {periodDateLine !== null ? (
              <div id="form-period-range" style={{ fontSize: 12, color: "var(--color-text-3)" }}>
                {periodDateLine}
              </div>
            ) : null}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <FormSaveIndicator />
          {/* D9 — "Önizleme"/yoğunluk toggle'ları client'ta hiç RENDER EDİLMEZ
              (internal-özgü üretim/QA araçları, disabled değil DOM'dan yok). */}
          {!isClient && (
            <>
              <button
                onClick={toggleUiDensity}
                title={
                  uiDensity === "compact"
                    ? "Rahat modu aç (daha geniş boşluklar)"
                    : "Kompakt modu aç (daha az boşluk)"
                }
                aria-label="Yoğunluk (density) modu"
                className="px-2 py-1.5 rounded-lg text-xs"
                style={{
                  background: "transparent",
                  border: "1px solid var(--color-border)",
                  color: "var(--color-text-2)",
                  minWidth: 34,
                }}
              >
                {uiDensity === "compact" ? "⇆" : "↔"}
              </button>
              <button
                onClick={() => {
                  const next = new URLSearchParams(searchParams);
                  if (isPreview) next.delete("preview");
                  else next.set("preview", "1");
                  setSearchParams(next);
                }}
                title={
                  isPreview
                    ? "Önizleme modu aktif — düzenlemeye dönmek için tıklayın"
                    : "Readonly önizleme: formu tıpkı assignee gibi gör, veri değiştirme"
                }
                className="px-3 py-1.5 rounded-lg text-xs"
                style={{
                  background: isPreview ? "var(--color-primary)" : "transparent",
                  border: "1px solid var(--color-primary)",
                  color: isPreview ? "var(--color-surface)" : "var(--color-primary)",
                  fontWeight: isPreview ? 600 : 400,
                }}
              >
                {isPreview ? "✓ Önizleme Aktif" : "Önizleme"}
              </button>
            </>
          )}
          {/* #027 — "Mevcut Hali İndir" kaldırıldı (Q3 kararı ile tutarlı).
              ProjectDetail'da da kaldırılmıştı; bu sayfada da gereksiz.
              Final rapor için "Final Raporu İndir" butonu var (tüm bölüm approved). */}
          <button
            onClick={() => setMissingOpen(true)}
            data-testid="missing-badge"
            title={stuckUnsavedCount > 0 ? FORM_SAVE_COPY.missingUnsavedTitle : undefined}
            className="px-3 py-1.5 rounded-lg text-xs"
            style={{
              background:
                missingCount > 0
                  ? "color-mix(in srgb, var(--color-error) 10%, white)"
                  : "var(--color-bg)",
              border: `1px solid ${missingCount > 0 ? "color-mix(in srgb, var(--color-error) 35%, white)" : "var(--color-border)"}`,
              color: missingCount > 0 ? "var(--color-error)" : "var(--color-text-2)",
            }}
          >
            {missingCount > 0
              ? FORM_SAVE_COPY.missingBadge(missingCount)
              : FORM_SAVE_COPY.missingNone}
            {stuckUnsavedCount > 0 && FORM_SAVE_COPY.missingUnsavedSuffix(stuckUnsavedCount)}
          </button>
          {isClient ? (
            // D8 — HER ZAMAN aktif (missingCount>0 diye disabled OLMAZ, AI butonundan
            // farklı davranış); submit-in-flight'ta çift-tık koruması var (submittingRef).
            <button
              onClick={() => void handleSubmitClick()}
              disabled={submitting}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ background: "var(--color-primary)", color: "var(--color-surface)" }}
            >
              {submitting ? "Kontrol ediliyor…" : "Formu Tamamla ve Gönder"}
            </button>
          ) : (
            <button
              onClick={() => {
                void flushNow();
                setGenerateModalOpen(true);
              }}
              disabled={missingCount > 0}
              title={
                missingCount > 0
                  ? `Zorunlu ${missingCount.toString()} alan eksik`
                  : "AI üretimini başlat"
              }
              className="px-3 py-1.5 rounded-lg text-xs font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ background: "var(--color-primary)", color: "var(--color-surface)" }}
            >
              AI Üretimi Başlat
            </button>
          )}
        </div>
      </header>

      {isClient && submitError && (
        <div
          role="alert"
          className="flex items-center justify-between gap-3"
          style={{
            padding: "8px 24px",
            background: "color-mix(in srgb, var(--color-error) 10%, white)",
            borderBottom: "1px solid color-mix(in srgb, var(--color-error) 35%, white)",
            color: "var(--color-error)",
            fontSize: "0.82rem",
          }}
        >
          <span>{submitError}</span>
          <button
            onClick={() => setSubmitError(null)}
            aria-label="Kapat"
            style={{
              background: "transparent",
              border: "none",
              color: "inherit",
              cursor: "pointer",
            }}
          >
            ×
          </button>
        </div>
      )}

      {isClient && infoBannerVisible && (
        <div
          role="status"
          className="flex items-center justify-between gap-3"
          style={{
            padding: "8px 24px",
            background: "color-mix(in srgb, var(--color-primary) 8%, white)",
            borderBottom: "1px solid var(--color-border)",
            color: "var(--color-text-2)",
            fontSize: "0.82rem",
          }}
        >
          <span>Bu form danışmanınız tarafından hazırlandı; cevaplarınız otomatik kaydedilir.</span>
          <button
            onClick={() => {
              setInfoBannerVisible(false);
              window.localStorage.setItem(CLIENT_INFO_BANNER_KEY, "1");
            }}
            aria-label="Kapat"
            style={{
              background: "transparent",
              border: "none",
              color: "inherit",
              cursor: "pointer",
            }}
          >
            ×
          </button>
        </div>
      )}

      {/* Fix Turu 1 (IMPORTANT-2) — design-ref Mobil kararı D7, D9'un hemen altında,
          aynı stil token'ları. Sadece dar viewport'ta mount anında görünür (bkz.
          wideScreenHintVisible init — matchMedia). */}
      {isClient && wideScreenHintVisible && (
        <div
          role="status"
          className="flex items-center justify-between gap-3"
          style={{
            padding: "8px 24px",
            background: "color-mix(in srgb, var(--color-primary) 8%, white)",
            borderBottom: "1px solid var(--color-border)",
            color: "var(--color-text-2)",
            fontSize: "0.82rem",
          }}
        >
          <span>Uzun formu geniş ekranda doldurmak daha rahat olabilir.</span>
          <button
            onClick={() => {
              setWideScreenHintVisible(false);
              window.localStorage.setItem(CLIENT_WIDESCREEN_HINT_KEY, "1");
            }}
            aria-label="Kapat"
            style={{
              background: "transparent",
              border: "none",
              color: "inherit",
              cursor: "pointer",
            }}
          >
            ×
          </button>
        </div>
      )}

      {/* Köken bandı + çipler (2A/8A/17A) — header'ın hemen altı. Client dalında
          hiç render edilmez (D9 deseni: disabled değil, DOM'da yok). */}
      {!isClient && (
        <ProvenanceBar
          provenance={provenance}
          provenanceError={provenanceError}
          onReload={() => void reloadProvenance()}
          filter={provenanceFilter}
          onFilterChange={setProvenanceFilter}
          counts={derived.chipCounts}
          totalQuestions={importableTotal}
          canOpenReport={canOpenReport}
        />
      )}

      {!isClient && <PresenceBanner others={presenceOthers} />}

      {/* 3-kolon — 2A: köken bandı (ve mevcut bantlar) auto-height; çalışma alanı
          kalan yüksekliği alır. Sabit `calc(100vh - 64px)` yalnız üst barı sayıyordu,
          bant eklenince sayfa taşardı. `minHeight: 0` kolon içi scroll'un çalışması
          için şart (flex item varsayılan min-height:auto taşmayı engeller). */}
      <div style={{ display: "flex", flex: 1, minHeight: 0 }}>
        <SectionNav
          sections={sections}
          questions={questions}
          activeSectionId={activeSectionId}
          onSelect={setActiveSection}
          saveState={saveState}
          filterCounts={derived.sectionFilterCounts}
        />

        <main
          style={{
            flex: 1,
            overflowY: "auto",
            padding: "24px 32px",
            background: isPreview ? "var(--color-bg)" : "transparent",
          }}
        >
          {isPreview && (
            <div
              role="status"
              style={{
                maxWidth: 820,
                margin: "0 auto 16px auto",
                padding: "8px 14px",
                borderRadius: 8,
                background: "color-mix(in srgb, var(--color-primary) 14%, white)",
                color: "var(--color-primary)",
                fontSize: "0.82rem",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                border: "1px solid var(--color-primary)",
              }}
            >
              <span>
                👁 <strong>Önizleme modu</strong> — veri değiştirilemez; assignee ne görüyorsa o.
              </span>
              <button
                onClick={() => {
                  const next = new URLSearchParams(searchParams);
                  next.delete("preview");
                  setSearchParams(next);
                }}
                style={{
                  fontSize: "0.75rem",
                  padding: "3px 10px",
                  border: "1px solid var(--color-primary)",
                  borderRadius: 4,
                  background: "var(--color-surface)",
                  color: "var(--color-primary)",
                  cursor: "pointer",
                }}
              >
                Düzenlemeye Dön
              </button>
            </div>
          )}
          {/* 2D — çeyreklik projede geçen yılın aynı dönemi hiç yoksa tek seferlik
              muted satır. Kapatma proje bazlı localStorage'da tutulur. */}
          {priorPeriodNote !== null ? (
            <div
              role="status"
              className="flex items-center justify-between gap-3"
              style={{
                maxWidth: 820,
                margin: "0 auto 12px auto",
                fontSize: "0.78rem",
                color: "var(--color-text-3)",
              }}
            >
              <span>{priorPeriodNote}</span>
              <button
                onClick={() => {
                  setPriorPeriodNoteDismissed(true);
                  try {
                    window.localStorage.setItem(`${PERIOD_HINT_DISMISS_PREFIX}${id}`, "1");
                  } catch {
                    // quota/private mode — satır yalnız bu oturumda kapanır
                  }
                }}
                aria-label="Kapat"
                style={{
                  background: "transparent",
                  border: "none",
                  color: "inherit",
                  cursor: "pointer",
                }}
              >
                ×
              </button>
            </div>
          ) : null}
          <fieldset
            disabled={isPreview}
            style={{
              maxWidth: 820,
              margin: "0 auto",
              border: "none",
              padding: 0,
              opacity: isPreview ? 0.95 : 1,
              pointerEvents: isPreview ? "none" : "auto",
            }}
          >
            {provenanceFilter !== "all" && !isClient ? (
              /* 2A — çip aktifken bölüm filtresi devre dışı: bölümler-arası düz liste. */
              <ProvenanceFilterList
                filter={provenanceFilter}
                questions={filteredQuestions}
                sections={sections}
                onClearFilter={() => setProvenanceFilter("all")}
                onPin={setPinnedCode}
                renderQuestion={(q) => <QuestionCard question={q} prov={derived.card} />}
              />
            ) : activeQuestions.length === 0 ? (
              <p style={{ color: "var(--color-text-3)" }}>Bu bölümde görünür soru yok.</p>
            ) : (
              activeQuestions.map((q) => (
                <QuestionCard key={q.id} question={q} prov={derived.card} />
              ))
            )}
          </fieldset>
          <SectionPager
            sections={sections}
            activeSectionId={activeSectionId}
            onSelect={setActiveSection}
            showButtons={provenanceFilter === "all" || isClient}
          />
        </main>

        <MissingPanelAside />
      </div>

      {/* Missing modal (üst bar butonu) */}
      {missingOpen && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.4)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 100,
          }}
          onClick={() => setMissingOpen(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="rounded-xl p-6 max-w-xl w-full"
            style={{
              background: "var(--color-surface)",
              border: "1px solid var(--color-border)",
              maxHeight: "80vh",
              overflowY: "auto",
            }}
          >
            <div className="flex items-center justify-between mb-4">
              <h2
                style={{
                  fontFamily: "var(--font-display)",
                  fontSize: "1.2rem",
                  color: "var(--color-text)",
                }}
              >
                Eksik Alanlar
              </h2>
              <button
                onClick={() => setMissingOpen(false)}
                aria-label="Kapat"
                style={{
                  background: "transparent",
                  border: "none",
                  fontSize: "1.2rem",
                  color: "var(--color-text-3)",
                  cursor: "pointer",
                }}
              >
                ×
              </button>
            </div>
            {/* M15 DR5 — gezinmeden önce modal kapanır ki hedef kart görünür olsun */}
            <MissingPanel onNavigate={() => setMissingOpen(false)} />
          </div>
        </div>
      )}

      {/* Conflict modal — eng-review D13: mevcut teknik banner ("Başka bir kullanıcı
          güncelleme yaptı" + soru kodu listesi) client'a gösterilmez, sade dil. */}
      {saveState === "conflict" &&
        conflictDetails &&
        (isClient ? (
          <ClientConflictModal onRefresh={() => void dismissConflict()} />
        ) : (
          <ConflictModal details={conflictDetails} onRefresh={() => void dismissConflict()} />
        ))}

      {isClient ? (
        submitModalOpen && (
          <SubmitConfirmModal
            missingCount={missingCount}
            submitting={submitting}
            onConfirm={() => void handleConfirmSubmit()}
            onCancel={() => setSubmitModalOpen(false)}
            onViewMissing={() => {
              setSubmitModalOpen(false);
              setMissingOpen(true);
            }}
          />
        )
      ) : (
        <GenerateConfirmModal
          projectId={id}
          open={generateModalOpen}
          onClose={() => setGenerateModalOpen(false)}
          onStarted={() => {
            setGenerateModalOpen(false);
            void navigate(`/projects/${id}/generate/progress`);
          }}
        />
      )}
    </div>
  );
}

// React.memo — FormFill her `saveState` değişiminde (idle→typing→saving→saved) yeniden
// render ediliyor; memo olmadan bu her seferinde TÜM kartları yeniden çizerdi.
// Props stabil: `question` snapshot'tan, `prov` üst seviyede useMemo'dan gelir.
const QuestionCard = memo(function QuestionCard({
  question,
  prov,
}: {
  question: QuestionSnapshot;
  /** Köken bağlamı; köken yoksa (ya da client aktörde) `null`. */
  prov: ProvenanceCardContext | null;
}): JSX.Element {
  const value = useFormFillStore((s) => selectValue(s, question.code));
  const note = useFormFillStore((s) => selectNote(s, question.code));
  const setValue = useFormFillStore((s) => s.setValue);
  const setNote = useFormFillStore((s) => s.setNote);
  // D9/D11 — PriorYearHint client'ta render edilmez (/prior-year client'a kapalı,
  // form-fill-store.load bu modda hiç çağırmıyor → priorYearByCode zaten boş kalır;
  // burada AYRICA explicit guard — veri kaynağına bağımlı örtük davranışa güvenmemek
  // için, DOM'da hiç bulunmama garantisini koddan okunur kılar).
  const isClient = useAuthStore((s) => s.user?.userKind === "client");
  // M15 §7.1 — tip uyum kapısı: soru tipi yıllar arası değiştiyse öneri kutusu hiç
  // görünmez (eski şekli kopyalamak anlaşılmaz VALIDATION_FAILED üretiyordu).
  const priorEntry = useFormFillStore((s) => {
    if (isClient || question.priorYearHintDisabled) return undefined;
    const entry = s.priorYearByCode[question.code];
    return entry?.questionType === question.type ? entry : undefined;
  });
  // K6 — önceki yıla göre olağan dışı sayı uyarısı (bloklamaz; client'ta priorEntry yok).
  const outlierText = useOutlierNotice(question.type, priorEntry, value);
  // M16 T21 — KPI tutarlılık ipucu: yalnız iç aktör + sayı/metin soru (client'ta DOM'da yok).
  // Finar 14.4 (K4): percent (dağılım) sorusunda yalnız `unit_mismatch` notu görünür — tek sayı
  // önerisi bu tipe yazılamaz.
  const kpiEntry = useFormFillStore((s) => {
    if (isClient) return undefined;
    const e = s.kpiConsistencyByCode[question.code];
    if (question.type === "numeric" || question.type === "text") return e;
    if (question.type === "percent" && e?.kind === "unit_mismatch") return e;
    return undefined;
  });
  // M15 DR6 — soru-düzeyi hata satırları (partKey'liler CompositeRenderer'da,
  // parçanın altında gösterilir; burada yalnızca partKey'siz olanlar).
  // Selector ham referans döner (her çağrıda yeni dizi üretmek tüm kartları
  // her tuşta re-render ederdi); filtre render içinde.
  const allErrors = useFormFillStore((s) => s.errorsByCode[question.code]);
  const questionErrors = (allErrors ?? []).filter((e) => e.partKey === undefined);
  // M15 DR8 — composite kart başlığında tekil "Zorunlu" yerine özet rozet
  // ("N zorunlu alan"); parça başlıklarında tekil rozet yok (dört kırmızı pill olurdu).
  const requiredPartCount =
    question.type === "composite"
      ? (readCompositeParts(question.options)?.filter((p) => p.required).length ?? 0)
      : 0;

  // --- Köken (Task 15). `prov === null` → köken yok ya da client aktör: tüm köken
  // UI'sı DOM'dan çıkar. Map/threshold/dosya listesi üst seviyede BİR KEZ hesaplanır
  // (fix round 1: kart başına `entries.find`/`imports.find` taraması kalktı).
  const resolvedExplicitly = useFormFillStore((s) => s.resolvedRejections.has(question.code));
  const markRejectionResolved = useFormFillStore((s) => s.markRejectionResolved);
  const responseUpdatedAt = useFormFillStore((s) => s.responsesByCode[question.code]?.updatedAt);
  const provEntry = prov?.entriesByCode.get(question.code);
  const lineKind = provenanceLineKind(provEntry, prov?.threshold ?? 1, value, resolvedExplicitly);
  const entryImport = provEntry ? (prov?.importsById.get(provEntry.importId) ?? null) : null;
  // 10A/G3 — "düzenlendi" YALNIZ zaman damgasıyla belirlenir (FormResponse DTO'sunda
  // `updatedBy` yok; kullanıcı karşılaştırması yapılamaz ve yapılmamalı).
  const importFinishedAt = entryImport?.finishedAt ?? prov?.createFinishedAt ?? null;
  const editedAfterImport =
    provEntry !== undefined &&
    responseUpdatedAt !== undefined &&
    importFinishedAt !== null &&
    isEditedAfterImport(responseUpdatedAt, importFinishedAt);

  return (
    <div
      data-question-code={question.code}
      className="ff-question-card mb-5 rounded-xl p-5"
      style={{
        background: "var(--color-surface)",
        border: "1px solid var(--color-border)",
      }}
    >
      {/* G16 — etiket satırı: sol grup `etiket → Zorunlu → s.136 → düzenlendi`
          (14A sırası, flex-wrap), soru kodu sağda. Rozet `<a>` olabildiği için
          `<label>` İÇİNE girmez; `.ff-question-label` sınıfı (compact density
          seçicisi) bu dış div'de kalır. */}
      <div className="ff-question-label flex items-start justify-between gap-3 mb-1">
        <div className="flex flex-wrap items-center gap-2">
          <label
            style={{
              fontSize: "0.95rem",
              color: "var(--color-text)",
              fontWeight: 500,
              lineHeight: 1.4,
            }}
          >
            {question.label}
          </label>
          {question.required && (
            <span
              style={{
                padding: "1px 8px",
                borderRadius: 999,
                background: "var(--color-bg)",
                color: "var(--color-error)",
                fontSize: "0.68rem",
                fontWeight: 600,
              }}
            >
              {question.type === "composite" && requiredPartCount > 0
                ? `${requiredPartCount} zorunlu alan`
                : "Zorunlu"}
            </span>
          )}
          {/* C4 (inceleme) — kısmi kabul edilen yanıt da rozetini KORUR: rapora giden
              link + ekran okuyucu alıntısı yalnız rozette var; kısmi satır altta çizilir. */}
          {(lineKind === "badge" || lineKind === "partial") && provEntry ? (
            <ProvenanceBadge
              entry={provEntry}
              importSummary={entryImport}
              canOpenReport={prov?.canOpenReport === true}
            />
          ) : null}
          {editedAfterImport ? (
            <span
              style={{
                padding: "1px 8px",
                borderRadius: 999,
                background: "var(--color-bg)",
                color: "var(--color-text-2)",
                border: "1px solid var(--color-border)",
                fontSize: "0.68rem",
                fontWeight: 600,
                whiteSpace: "nowrap",
              }}
            >
              {IMPORT_COPY.editedLabel}
            </span>
          ) : null}
        </div>
        <span
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: "0.7rem",
            color: "var(--color-text-3)",
            whiteSpace: "nowrap",
          }}
        >
          {question.code}
        </span>
      </div>
      {question.description && (
        <p style={{ color: "var(--color-text-2)", fontSize: "0.82rem", marginBottom: 8 }}>
          {question.description}
        </p>
      )}
      <div className="mt-2">
        <QuestionInput
          question={question}
          value={value}
          onChange={(v) => setValue(question.code, v)}
        />
      </div>
      {outlierText !== null && <OutlierNotice text={outlierText} />}
      <SupersededNotice question={question} />
      {/* UX P1 K4 — sunucu reddi: değer kartta kalır ama kaydedilmedi; düzeltince gider. */}
      {(allErrors?.length ?? 0) > 0 && (
        <p
          className="mt-1"
          data-testid="question-unsaved"
          style={{ color: "var(--color-error)", fontSize: "0.78rem", fontWeight: 600 }}
        >
          {FORM_SAVE_COPY.cardUnsaved}
        </p>
      )}
      {questionErrors.map((e, i) => (
        <p
          key={i}
          className="mt-1"
          style={{ color: "var(--color-error)", fontSize: "0.78rem" }}
          role="alert"
        >
          {e.message}
        </p>
      ))}
      {/* 13A kart içi sabit sıra: input → validation → KÖKEN SATIRI → helperText →
          önceki yıl → kanıt → not. `badge` türünde burada bir şey çizilmez. */}
      {lineKind !== null && lineKind !== "badge" && provEntry ? (
        <ProvenanceLine
          kind={lineKind}
          entry={provEntry}
          question={question}
          // F7 — rozet çizildiyse kısmi satırda ref tekrar edilmez.
          badgeShown={lineKind === "partial"}
          onUseProposal={() => {
            setValue(question.code, proposalFormValue(provEntry.proposedValue, question));
            markRejectionResolved(question.code);
          }}
        />
      ) : null}
      {question.helperText && (
        <p
          className="mt-2"
          style={{ color: "var(--color-text-3)", fontSize: "0.78rem", fontStyle: "italic" }}
        >
          {question.helperText}
        </p>
      )}
      {priorEntry && (
        <PriorYearHint
          entry={priorEntry}
          question={question}
          onCopy={() => setValue(question.code, priorEntry.value)}
          /* D10/R17 — 13A "köken SATIRI varken": yalnız kart içinde gerçek bir köken
             satırı (düşük güven / aktarılmadı / öneri kabul) çizildiğinde daraltılır.
             Yalnız rozet varken (yüksek güven) satır yok → öneri tam kartıyla kalır.
             Aktarılmamış projelerde `false` → markup/metin birebir korunur. */
          collapsed={lineKind !== null && lineKind !== "badge"}
        />
      )}
      {kpiEntry && (
        <KpiConsistencyHint
          entry={kpiEntry}
          question={question}
          /* B-01: form yanıtı ÇIPLAK değerdir (NumericRenderer `onChange(n)` ile aynı şekil;
             `{type, value}` sarmalayıcısı sunucuda 422 veriyordu). K4: `v` = `suggestedValue`
             (soru biriminde, gerekirse çevrilmiş). */
          onUseKpi={(v) =>
            setValue(question.code, question.type === "numeric" ? v : formatKpiNumber(v))
          }
        />
      )}
      {question.allowEvidence && question.type !== "file" && <EvidenceUpload question={question} />}
      {/* 2026-09-30 (Finar) — danışman notu client'ta HİÇ render edilmez (D9 deseni):
          danışmanın iç notu + placeholder'ı "AI üretimine bağlam…" diyordu. */}
      {question.allowNote && !isClient && (
        <ConsultantNote
          questionCode={question.code}
          value={note}
          onChange={(v) => setNote(question.code, v)}
        />
      )}
    </div>
  );
});

function ConflictModal({
  details,
  onRefresh,
}: {
  details: { questionCode: string; message: string; serverUpdatedAt?: string }[];
  onRefresh: () => void;
}): JSX.Element {
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.4)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 100,
      }}
    >
      <div
        className="rounded-xl p-6 max-w-md"
        style={{
          background: "var(--color-surface)",
          border: "1px solid var(--color-border)",
        }}
      >
        <h2
          style={{
            fontFamily: "var(--font-display)",
            color: "var(--color-error)",
            fontSize: "1.2rem",
            marginBottom: 8,
          }}
        >
          Başka bir kullanıcı güncelleme yaptı
        </h2>
        <p style={{ color: "var(--color-text-2)", fontSize: "0.9rem", marginBottom: 12 }}>
          Son kaydınızdan sonra aşağıdaki sorular başka bir yerde güncellenmiş. Yenilemek istiyor
          musunuz? (Lokal değişiklikleriniz silinecek.)
        </p>
        <ul style={{ fontSize: "0.82rem", color: "var(--color-text-3)", marginBottom: 16 }}>
          {details.slice(0, 5).map((d) => (
            <li key={d.questionCode} style={{ marginBottom: 4 }}>
              <code style={{ fontFamily: "var(--font-mono)" }}>{d.questionCode}</code> — {d.message}
            </li>
          ))}
        </ul>
        <button
          onClick={onRefresh}
          className="w-full rounded-lg px-4 py-2 text-sm font-semibold"
          style={{ background: "var(--color-primary)", color: "var(--color-surface)" }}
        >
          Yenile
        </button>
      </div>
    </div>
  );
}

// Müşteri-dili conflict modalı (eng-review D13) — "başka bir kullanıcı"/soru kodu
// listesi gibi teknik detay YOK, tek cümle + Yenile.
function ClientConflictModal({ onRefresh }: { onRefresh: () => void }): JSX.Element {
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.4)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 100,
      }}
    >
      <div
        className="rounded-xl p-6 max-w-md"
        style={{
          background: "var(--color-surface)",
          border: "1px solid var(--color-border)",
        }}
      >
        <p style={{ color: "var(--color-text)", fontSize: "0.95rem", marginBottom: 16 }}>
          Danışmanınız bu cevabı güncelledi — sayfayı yenileyin.
        </p>
        <button
          onClick={onRefresh}
          className="w-full rounded-lg px-4 py-2 text-sm font-semibold"
          style={{ background: "var(--color-primary)", color: "var(--color-surface)" }}
        >
          Yenile
        </button>
      </div>
    </div>
  );
}

// Müşteri submit confirm modalı (design-ref D8) — eksik zorunlu alan varken kilit
// YOK, sadece uyarı satırı + eksik listesine link.
function SubmitConfirmModal({
  missingCount,
  submitting,
  onConfirm,
  onCancel,
  onViewMissing,
}: {
  missingCount: number;
  submitting: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  onViewMissing: () => void;
}): JSX.Element {
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.4)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 100,
      }}
      onClick={() => !submitting && onCancel()}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="rounded-xl p-6 max-w-md w-full"
        style={{
          background: "var(--color-surface)",
          border: "1px solid var(--color-border)",
        }}
      >
        <h2
          style={{
            fontFamily: "var(--font-display)",
            color: "var(--color-text)",
            fontSize: "1.2rem",
            marginBottom: 8,
          }}
        >
          Formu Gönder
        </h2>
        <p style={{ color: "var(--color-text-2)", fontSize: "0.9rem", marginBottom: 12 }}>
          Gönderdikten sonra forma erişiminiz kapanır; danışmanınız gerekirse yeniden açabilir.
        </p>
        {missingCount > 0 && (
          <div
            style={{
              padding: "10px 12px",
              borderRadius: 8,
              background: "color-mix(in srgb, var(--color-warning) 10%, white)",
              border: "1px solid color-mix(in srgb, var(--color-warning) 35%, white)",
              color: "var(--color-text-2)",
              fontSize: "0.85rem",
              marginBottom: 16,
            }}
          >
            <p style={{ marginBottom: 6 }}>
              <strong>{missingCount}</strong> zorunlu alan boş — yine de gönderebilirsiniz;
              danışmanınız gerekirse sizinle iletişime geçer.
            </p>
            <button
              onClick={onViewMissing}
              style={{
                background: "transparent",
                border: "none",
                padding: 0,
                color: "var(--color-primary)",
                fontSize: "0.82rem",
                textDecoration: "underline",
                cursor: "pointer",
              }}
            >
              Eksik alanları göster
            </button>
          </div>
        )}
        <div className="flex gap-3 justify-end">
          <button
            onClick={onCancel}
            disabled={submitting}
            className="rounded-lg px-4 py-2 text-sm disabled:opacity-50"
            style={{
              background: "transparent",
              border: "1px solid var(--color-border)",
              color: "var(--color-text-2)",
            }}
          >
            Vazgeç
          </button>
          <button
            onClick={onConfirm}
            disabled={submitting}
            className="rounded-lg px-4 py-2 text-sm font-semibold disabled:opacity-50"
            style={{ background: "var(--color-primary)", color: "var(--color-surface)" }}
          >
            {submitting ? "Gönderiliyor…" : "Gönder"}
          </button>
        </div>
      </div>
    </div>
  );
}
